import {
  ArrowRight,
  BrainCircuit,
  Bug,
  CalendarClock,
  CheckCircle2,
  Database,
  GitBranch,
  ListChecks,
  PlayCircle,
  RadioTower,
  ScanEye,
  ShieldCheck,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import type { AgentDefinition, AgentRun } from "../types";

const agentIcons: Record<string, LucideIcon> = {
  perception: ScanEye,
  memory: Database,
  reasoning: BrainCircuit,
  forecast: TrendingUp,
  scheduler: CalendarClock,
  critic: ShieldCheck,
  coordinator: RadioTower,
  qa: Bug,
};

const pipeline: Array<{ name: string; icon: LucideIcon }> = [
  { name: "Perception", icon: ScanEye },
  { name: "Forecast", icon: TrendingUp },
  { name: "Vector Memory", icon: Database },
  { name: "Pioneer", icon: BrainCircuit },
  { name: "Scheduler", icon: CalendarClock },
  { name: "Critic", icon: ShieldCheck },
  { name: "Coordinator", icon: RadioTower },
];

const integrationDetails = [
  {
    owner: "Guild AI",
    icon: GitBranch,
    title: "Execution order, permissions and audit trail",
    body:
      "The Orchestrator opens one session across Perception, Forecast, Vector Memory, Pioneer Reasoning, Scheduler, Critic, and Coordinator. Every agent receives the minimum tool scope, and a candidate plan cannot be activated before Critic approval.",
    contract: "agent run, tool call, credential scope, version, rollback",
  },
  {
    owner: "Pioneer",
    icon: PlayCircle,
    title: "Cause ranking and learning from worker corrections",
    body:
      "Pioneer receives live queues, equipment, inventory and retrieved history evidence, then ranks causes and candidate actions as structured JSON. A pass or manager correction is attached to the inference ID, producing a restaurant-specific candidate model; only evaluated versions are promoted.",
    contract: "cause, ranked_actions, confidence, inference_id, correction",
  },
  {
    owner: "Firestore Vector",
    icon: Database,
    title: "Evidence-backed operating episode memory",
    body:
      "Firestore Vector stores normal history and delayed history with item, station, bookings, queues, staffing, equipment, inventory, and the actual 30–60 minute outcome. Metadata narrows the search before vector retrieval returns the closest operational evidence.",
    contract: "situation vector + metadata → similar episodes + outcomes",
  },
  {
    owner: "Replay",
    icon: ShieldCheck,
    title: "QA for the deployed user journey",
    body:
      "Replay is deliberately outside the operating decision loop. It records anonymous sign-in, 3D load, band double-press, recommendation, accept/pass, replacement task and Firestore update on the deployed URL, then reports the failing step and root cause.",
    contract: "deployed URL + journey → recording, failure, root cause",
  },
];

export function AgentsView({
  agents,
  runs,
}: {
  agents: AgentDefinition[];
  runs: AgentRun[];
}) {
  return (
    <main className="content-page agents-page">
      <header className="content-heading">
        <span>AGENT CONTROL PLANE</span>
        <h1>Separate decisions. Trace every action.</h1>
        <p>
          Numeric scheduling remains deterministic. Specialized services own reasoning, memory,
          feedback and orchestration.
        </p>
      </header>

      <section className="agent-pipeline" aria-label="Agent execution order">
        {pipeline.map(
          ({ name, icon: Icon }, index, list) => (
            <div key={name}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <Icon className="pipeline-agent-icon" size={17} strokeWidth={1.9} aria-hidden="true" />
              <strong>{name}</strong>
              {index < list.length - 1 ? (
                <ArrowRight className="pipeline-arrow" size={17} aria-hidden="true" />
              ) : null}
            </div>
          ),
        )}
      </section>

      <section className="agent-registry">
        <div className="section-title">
          <div>
            <span>GUILD REGISTRY</span>
            <h2>Agents and permissions</h2>
          </div>
          <small>Firestore is authoritative; external adapters activate when configured</small>
        </div>
        {agents.map((agent) => {
          const AgentIcon = agentIcons[agent.id] ?? ListChecks;

          return (
            <article key={agent.id}>
              <div className="agent-name">
                <span className={`agent-role-icon agent-role-icon--${agent.id}`}>
                  <AgentIcon size={20} strokeWidth={1.9} aria-hidden="true" />
                  <i className={agent.status} title={`Status: ${agent.status}`} />
                </span>
                <div>
                  <strong>{agent.name}</strong>
                  <span>{agent.owner}</span>
                </div>
              </div>
              <p>{agent.role}</p>
              <div className="agent-contract">
                <span><b>IN</b> {agent.input}</span>
                <span><b>OUT</b> {agent.output}</span>
              </div>
              <em>{agent.permission}</em>
            </article>
          );
        })}
      </section>

      <section className="integration-plan">
        <div className="section-title">
          <div>
            <span>PROVIDER PLAN</span>
            <h2>How each provider is used</h2>
          </div>
        </div>
        {integrationDetails.map(({ owner, icon: Icon, title, body, contract }) => (
          <article key={owner}>
            <Icon size={22} />
            <div>
              <span>{owner}</span>
              <h3>{title}</h3>
              <p>{body}</p>
              <code>{contract}</code>
            </div>
          </article>
        ))}
      </section>

      <section className="agent-runs">
        <div className="section-title">
          <div>
            <span>LIVE TRACE</span>
            <h2>Recent runs</h2>
          </div>
        </div>
        {runs.map((run) => (
          <div key={run.id}>
            <CheckCircle2 size={18} />
            <time>{run.time}</time>
            <strong>{run.agent}</strong>
            <span>{run.summary}</span>
            <em>{run.evidence ?? run.status}</em>
          </div>
        ))}
      </section>
    </main>
  );
}
